import React from 'react';
import { TestimonialCard } from '../ui/TestimonialCard';
import { BarChart3Icon, CoffeeIcon, DollarSignIcon } from 'lucide-react';
export const CaseStudy = () => {
  return <section className="py-16 md:py-20 border-t border-slate-200" data-id="element-342">
      <h2 className="text-2xl font-bold text-center text-slate-800 mb-10 px-4" data-id="element-343">
        Case Study: How NightPhase Transformed David's Professional Performance
      </h2>
      <div className="flex flex-col md:flex-row gap-8 mb-12" data-id="element-344">
        <div className="md:w-1/3" data-id="element-345">
          <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80" alt="Professional man working" className="w-full h-64 md:h-full object-cover rounded-xl shadow-md" data-id="element-346" />
        </div>
        <div className="md:w-2/3 bg-gradient-to-r from-indigo-50 to-slate-50 rounded-xl p-6 md:p-8" data-id="element-347">
          <div className="prose max-w-none text-slate-700" data-id="element-348">
            <p className="mb-4" data-id="element-349">
              David, a 48-year-old technology consultant, built his reputation
              on delivering complex projects under tight deadlines. But after
              two decades in the high-pressure tech industry, chronic sleep
              issues were threatening his edge.
            </p>
            <p className="mb-4" data-id="element-350">
              "I was the guy who would respond to emails at 2 AM because I
              couldn't sleep anyway," David explains. "I'd push through
              exhaustion with caffeine and willpower, but I knew it wasn't
              sustainable. My clarity in client meetings was suffering, and I
              started making small mistakes in my code reviews."
            </p>
            <p className="mb-6" data-id="element-351">
              After trying various sleep solutions with limited success, David
              was introduced to NightPhase Sleep Patches by a colleague. The
              results were tracked meticulously over 90 days:
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-6" data-id="element-352">
              <div className="bg-white p-5 rounded-lg shadow-sm" data-id="element-353">
                <div className="flex items-center mb-3" data-id="element-354">
                  <BarChart3Icon className="h-5 w-5 text-indigo-600 mr-2" data-id="element-355" />
                  <h4 className="font-semibold text-slate-800" data-id="element-356">
                    Sleep Quality
                  </h4>
                </div>
                <p className="text-slate-600 text-sm mb-2" data-id="element-357">
                  Before NightPhase:
                </p>
                <p className="text-slate-800 mb-3" data-id="element-358">
                  4.2/10 average sleep quality score
                </p>
                <p className="text-slate-600 text-sm mb-2" data-id="element-359">After 90 Days:</p>
                <p className="text-slate-800 font-medium" data-id="element-360">
                  8.7/10 average sleep quality score
                </p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-sm" data-id="element-361">
                <div className="flex items-center mb-3" data-id="element-362">
                  <CoffeeIcon className="h-5 w-5 text-indigo-600 mr-2" data-id="element-363" />
                  <h4 className="font-semibold text-slate-800" data-id="element-364">
                    Caffeine Dependency
                  </h4>
                </div>
                <p className="text-slate-600 text-sm mb-2" data-id="element-365">
                  Before NightPhase:
                </p>
                <p className="text-slate-800 mb-3" data-id="element-366">6-7 cups of coffee daily</p>
                <p className="text-slate-600 text-sm mb-2" data-id="element-367">After 90 Days:</p>
                <p className="text-slate-800 font-medium" data-id="element-368">
                  1-2 cups, consumed for enjoyment
                </p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-sm" data-id="element-369">
                <div className="flex items-center mb-3" data-id="element-370">
                  <DollarSignIcon className="h-5 w-5 text-indigo-600 mr-2" data-id="element-371" />
                  <h4 className="font-semibold text-slate-800" data-id="element-372">
                    Business Performance
                  </h4>
                </div>
                <p className="text-slate-600 text-sm mb-2" data-id="element-373">
                  Before NightPhase:
                </p>
                <p className="text-slate-800 mb-3" data-id="element-374">
                  Declining client retention rate
                </p>
                <p className="text-slate-600 text-sm mb-2" data-id="element-375">After 90 Days:</p>
                <p className="text-slate-800 font-medium" data-id="element-376">
                  22% increase in quarterly revenue
                </p>
              </div>
            </div>
            <p className="mb-6" data-id="element-377">
              The most significant change, according to David, was in his
              cognitive performance. "My ability to solve complex problems
              returned to levels I hadn't experienced since my thirties. I could
              hold entire system architectures in my head again, making
              connections that would have escaped me before."
            </p>
            <p data-id="element-378">
              David's case demonstrates the compounding effects of optimal sleep
              on professional performance. By addressing the root cause of his
              performance decline rather than masking symptoms, NightPhase
              provided a sustainable solution that translated directly to
              business results.
            </p>
          </div>
        </div>
      </div>
      <h3 className="text-xl font-semibold text-center text-slate-800 mb-8 px-4" data-id="element-379">
        What Other Executives Are Saying
      </h3>
      <div className="grid md:grid-cols-2 gap-8 px-4 md:px-0" data-id="element-380">
        <TestimonialCard quote="As a CEO, my mental clarity directly impacts hundreds of employees and millions in revenue. NightPhase has become my non-negotiable performance tool." name="Jonathan Reed" title="Chief Executive Officer" company="Vertex Technologies" imageSrc="https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=256&q=80" data-id="element-381" />
        <TestimonialCard quote="I've calculated that NightPhase delivers a 300% ROI for me monthly through increased productivity and better strategic decisions." name="Alexandra Chen" title="Investment Director" company="Meridian Capital" imageSrc="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=256&q=80" data-id="element-382" />
      </div>
    </section>;
};